import React, { useRef, useState, useEffect } from "react";
import logo from "../../assests/IVR.png";
import image1 from "../../assests/1.jpg";
import image2 from "../../assests/2.jpg";
import image3 from "../../assests/3.jpg";

import HeroSection from "../../components/Dashboard/AILandingPage/HeroSection";
import GallerySection from "../../components/Dashboard/AILandingPage/GallerySection";
import AboutSection from "../../components/Dashboard/AILandingPage/AboutSection";
import StepsSection from "../../components/Dashboard/AILandingPage/StepsSection";
import CTASection from "../../components/Dashboard/AILandingPage/CTASection";
import FooterSection from "../../components/Dashboard/AILandingPage/FooterSection";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const AILandingPage = () => {
  const navigate = useNavigate();
  const equalizerCanvasRef = useRef(null);
  const patternCanvas1Ref = useRef(null);
  const patternCanvas2Ref = useRef(null);
  const patternCanvas3Ref = useRef(null);

  const [galleryImages, setGalleryImages] = useState([
    { id: 1, image: image1, alt: "Beach", title: "Serene Beachfront Resorts" },
    { id: 2, image: image2, alt: "Lobby", title: "Opulent Hotel Lobbies" },
    { id: 3, image: image3, alt: "City", title: "Vibrant Cityscapes" },
  ]);

  const handleImageClick = (i) => {
    if (i === 0) return;
    const newImages = [...galleryImages];
    [newImages[0], newImages[i]] = [newImages[i], newImages[0]];
    setGalleryImages(newImages);
  };

  // --- CONSOLIDATED useEffect FOR ALL ANIMATIONS ---
  useEffect(() => {
    // --- 1. SETUP FOR ALL CANVASES AND ANIMATIONS ---
    const equalizerCanvas = equalizerCanvasRef.current;
    const eqCtx = equalizerCanvas.getContext("2d");
    let bars = [];
    const barCount = 60;
    const colors = [
      "#34D399",
      "#60A5FA",
      "#FBBF24",
      "#F87171",
      "#A78BFA",
      "#EC4899",
      "#FFFFFF",
    ];
    let eqAnimationId;
    let patternAnimationIds = {};

    // --- Equalizer Logic ---
    class Bar {
      constructor(x, y, width, height, color) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.color = color;
        this.targetHeight = height;
        this.speed = Math.random() * 0.5 + 0.1;
      }
      update() {
        if (Math.abs(this.height - this.targetHeight) < 1) {
          this.targetHeight =
            Math.random() * (equalizerCanvas.height * 0.6) + 20;
        }
        this.height += (this.targetHeight - this.height) * this.speed;
      }
      draw() {
        eqCtx.fillStyle = this.color;
        const r = this.width / 2;
        eqCtx.beginPath();
        eqCtx.moveTo(this.x + r, this.y);
        eqCtx.lineTo(this.x + this.width - r, this.y);
        eqCtx.arcTo(
          this.x + this.width,
          this.y,
          this.x + this.width,
          this.y + r,
          r
        );
        eqCtx.lineTo(this.x + this.width, this.y + this.height - r);
        eqCtx.arcTo(
          this.x + this.width,
          this.y + this.height,
          this.x + this.width - r,
          this.y + this.height,
          r
        );
        eqCtx.lineTo(this.x + r, this.y + this.height);
        eqCtx.arcTo(
          this.x,
          this.y + this.height,
          this.x,
          this.y + this.height - r,
          r
        );
        eqCtx.lineTo(this.x, this.y + r);
        eqCtx.arcTo(this.x, this.y, this.x + r, this.y, r);
        eqCtx.closePath();
        eqCtx.fill();
      }
    }
    const initBars = () => {
      bars = [];
      const barWidth = 12;
      const gap = 18;
      const totalWidth = (barWidth + gap) * barCount;
      const startX = (equalizerCanvas.width - totalWidth) / 2;
      for (let i = 0; i < barCount; i++) {
        const x = startX + i * (barWidth + gap);
        const height = Math.random() * (equalizerCanvas.height * 0.5) + 20;
        const color = colors[Math.floor(Math.random() * colors.length)];
        bars.push(new Bar(x, 0, barWidth, height, color));
      }
    };
    const animateEqualizer = () => {
      if (!equalizerCanvasRef.current) return;
      eqCtx.clearRect(0, 0, equalizerCanvas.width, equalizerCanvas.height);
      bars.forEach((bar) => {
        bar.update();
        bar.y = (equalizerCanvas.height - bar.height) / 2;
        bar.draw();
      });
      eqAnimationId = requestAnimationFrame(animateEqualizer);
    };
    const resizeEqualizer = () => {
      if (!equalizerCanvasRef.current) return;
      equalizerCanvas.width = window.innerWidth;
      equalizerCanvas.height = window.innerHeight;
      initBars();
    };

    // --- Generic Pattern Drawing Logic (to be called by the observer) ---
    const startPatternAnimation = (canvasRef, shape, w, h, id) => {
      if (!canvasRef.current || patternAnimationIds[id]) return; // Don't start if already running
      const ctx = canvasRef.current.getContext("2d");
      let frame = 0;
      const draw = () => {
        if (!canvasRef.current) return;
        const time = frame * 0.02;
        ctx.clearRect(0, 0, w, h);
        for (let x = 10; x < w; x += 20) {
          for (let y = 10; y < h; y += 20) {
            let colorIndex = -1,
              distance;
            switch (shape) {
              case "grid":
                distance = Math.hypot(x - w / 2, y - h / 2);
                colorIndex =
                  Math.floor(
                    (Math.sin(distance * 0.05 + time) + 1) * 0.5 * colors.length
                  ) % colors.length;
                break;
              case "flow":
                const noise =
                  (Math.sin(x * 0.05 + time) + Math.cos(y * 0.05 + time)) *
                    0.5 +
                  0.5;
                colorIndex = Math.floor(noise * colors.length);
                break;
              case "circle":
                distance = Math.hypot(x - w / 2, y - h / 2);
                if (distance > w * 0.3 && distance < w * 0.45) {
                  colorIndex =
                    Math.floor(
                      (Math.atan2(y - h / 2, x - w / 2) / (2 * Math.PI) +
                        0.5 +
                        time * 0.1) *
                        colors.length
                    ) % colors.length;
                }
                break;
              default:
                break;
            }
            if (colorIndex !== -1) {
              ctx.fillStyle = colors[colorIndex];
              ctx.beginPath();
              ctx.arc(x, y, 4, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
        frame++;
        patternAnimationIds[id] = requestAnimationFrame(draw);
      };
      draw();
    };
    const stopPatternAnimation = (id) => {
      if (patternAnimationIds[id]) {
        cancelAnimationFrame(patternAnimationIds[id]);
        delete patternAnimationIds[id];
      }
    };

    // --- 2. INTERSECTION OBSERVER FOR SCROLL TRIGGERS ---
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const targetCanvasRefs = [
            { ref: patternCanvas1Ref, id: "p1", shape: "grid", w: 500, h: 500 },
            { ref: patternCanvas2Ref, id: "p2", shape: "flow", w: 200, h: 200 },
            {
              ref: patternCanvas3Ref,
              id: "p3",
              shape: "circle",
              w: 200,
              h: 200,
            },
          ];
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
            entry.target
              .querySelectorAll("span.bg-clip-text")
              .forEach((span) => span.classList.add("opacity-100"));

            targetCanvasRefs.forEach((c) => {
              if (entry.target.contains(c.ref.current))
                startPatternAnimation(c.ref, c.shape, c.w, c.h, c.id);
            });
          } else {
            targetCanvasRefs.forEach((c) => {
              if (entry.target.contains(c.ref.current))
                stopPatternAnimation(c.id);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    const animatedElements = document.querySelectorAll(".scroll-animation");
    animatedElements.forEach((el) => observer.observe(el));

    // --- 3. INITIALIZE AND CLEANUP ---
    window.addEventListener("resize", resizeEqualizer);
    resizeEqualizer();
    animateEqualizer(); // Start the main background equalizer immediately

    return () => {
      window.removeEventListener("resize", resizeEqualizer);
      cancelAnimationFrame(eqAnimationId);
      Object.values(patternAnimationIds).forEach(cancelAnimationFrame);
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []); // Single, empty dependency array ensures this runs only once.

  return (
    <div className="antialiased bg-blue-900 text-slate-200">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-blue-900/60 border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo Section */}
          <div className="flex items-center">
            <img src={logo} alt="IVRVue Logo" className="h-10 w-auto" />
          </div>

          {/* Action Button */}
          <motion.button
            onClick={() => navigate("/voice-to-voice")}
            whileHover={{
              scale: 1.05,
              // Teal shadow rgba(20, 184, 166, 0.5)
              boxShadow: "0 0 20px rgba(20, 184, 166, 0.5)",
            }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            // Teal to Emerald gradient
            className="group relative px-8 py-3 bg-gradient-to-r from-teal-600 to-emerald-500 text-white text-sm font-bold rounded-full transition-all duration-300 shadow-[0_0_15px_rgba(20,184,166,0.3)] overflow-hidden"
          >
            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] transition-transform" />

            <span className="relative flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                {/* Teal dots */}
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-300 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-100"></span>
              </span>
              AI Voice Assistant
            </span>
          </motion.button>
        </div>
      </header>

      <main>
        <HeroSection equalizerCanvasRef={equalizerCanvasRef} />
        <GallerySection
          galleryImages={galleryImages}
          handleImageClick={handleImageClick}
        />
        <AboutSection patternCanvas1Ref={patternCanvas1Ref} />
        <StepsSection
          patternCanvas2Ref={patternCanvas2Ref}
          patternCanvas3Ref={patternCanvas3Ref}
        />
        <CTASection />
      </main>

      <FooterSection />
    </div>
  );
};

export default AILandingPage;
